import { useEffect } from 'react';
import { NavLink } from 'react-router-dom';
import { ListChecks, Search, Building2, DollarSign } from 'lucide-react';
import { useShellStore } from '@/stores/shell-store.js';
import { PHONE_BAR_ITEMS, phoneBarAction } from './doors.js';
import { NO_DOOR_HERE } from './words.js';

// THE PHONE'S BOTTOM BAR on the v3 account page (stage D4, the Mobile and
// MobileSections sketches): fixed to the bottom, the sketch's four items in
// its order, each a tap target of at least 44px. It renders only under
// PhoneLayout (so only under 640px) and, while mounted, owns the bottom in
// the shell store so the shell's BottomTabBar steps aside: one bar, never
// two stacked. An item is a button that opens its section or fires the
// palette's event, a link to a route the router holds, or, when
// phoneBarAction finds no door, a disabled control that says NO_DOOR_HERE
// in place; nothing here reads data or derives a state.

export const V3_BOTTOM_BAR = 'v3-account';
const ICONS = Object.freeze({ next: ListChecks, search: Search, accounts: Building2, money: DollarSign });
const ITEM_CLASS = 'flex h-full w-full flex-col items-center justify-center gap-0.5';

export default function PhoneBar({ openSection }) {
  const claim = useShellStore((s) => s.claimBottomBar);
  const release = useShellStore((s) => s.releaseBottomBar);
  useEffect(() => {
    claim(V3_BOTTOM_BAR);
    return () => release(V3_BOTTOM_BAR);
  }, [claim, release]);
  return (
    <nav aria-label="Account bar" className="fixed inset-x-0 bottom-0 z-30 border-t border-border bg-surface/95 backdrop-blur" style={{ paddingBottom: 'env(safe-area-inset-bottom)' }} data-phone-bar>
      <ul className="flex h-16">
        {PHONE_BAR_ITEMS.map((item) => {
          const Icon = ICONS[item.key];
          const action = phoneBarAction(item, { openSection });
          const tone = item.current ? 'font-semibold text-accent' : 'text-text-dim hover:text-text-muted';
          const inner = (
            <>
              <Icon size={20} strokeWidth={1.5} aria-hidden="true" />
              <span className="text-[11px] font-medium tracking-wide">{item.label}</span>
            </>
          );
          return (
            <li key={item.key} className="flex-1" data-phone-bar-item={item.key}>
              {!action ? (
                <button type="button" disabled aria-disabled="true" className={`min-h-[44px] ${ITEM_CLASS} text-text-dim opacity-60`} title={NO_DOOR_HERE} data-no-door>
                  {inner}
                  <span className="text-[9px] leading-none text-text-dim">{NO_DOOR_HERE}</span>
                </button>
              ) : action.to ? (
                <NavLink to={action.to} className={`min-h-[44px] ${ITEM_CLASS} ${tone}`} aria-current={item.current ? 'page' : undefined}>{inner}</NavLink>
              ) : (
                <button type="button" onClick={action.onClick} className={`min-h-[44px] ${ITEM_CLASS} ${tone}`} aria-current={item.current ? 'page' : undefined}>{inner}</button>
              )}
            </li>
          );
        })}
      </ul>
    </nav>
  );
}
